import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { Card, Button, WarningCallout, BackLink } from 'nhsuk-react-components';
import { requestEmergencyAccess } from '../../actions/emergencyAccessActions';

function EmergencyAccessRequestScreen() {
    const [nhsNumber, setNhsNumber] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const history = useHistory();

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    useEffect(() => {
        document.title = 'Emergency Access Request';
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        // Validate NHS number format (basic validation)
        const cleanedNHS = nhsNumber.replace(/\s/g, '');
        if (cleanedNHS.length !== 10 || !/^\d+$/.test(cleanedNHS)) {
            setError('Please enter a valid 10-digit NHS number');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await dispatch(requestEmergencyAccess(cleanedNHS, reason));
            history.push('/emergency/granted', { tokenData: response });
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to request emergency access. Please try again.');
            setIsSubmitting(false);
        }
    };

    // If not logged in, redirect to login with return URL
    if (!userInfo) {
        // Redirect to NHS SSO login with return URL
        history.push('/login?redirect=/emergency/request');
        return null;
    }

    return (
        <div className="nhsuk-width-container">
            <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                <Container fluid>
                    <BackLink href="/" onClick={(e) => { e.preventDefault(); history.push('/'); }}>
                        Go back
                    </BackLink>

                    <h1>Emergency Access Request</h1>

                    <WarningCallout>
                        <WarningCallout.Label>Important</WarningCallout.Label>
                        <p>
                            <strong>This feature is for clinical emergencies only.</strong>
                        </p>
                        <ul>
                            <li>All access requests are logged and audited</li>
                            <li>Unauthorized access is strictly prohibited</li>
                            <li>Misuse will result in serious consequences</li>
                            <li>Access is time-limited (1 hour) and single-use only</li>
                        </ul>
                    </WarningCallout>

                    {error && (
                        <div className="nhsuk-error-summary" aria-labelledby="error-summary-title" role="alert">
                            <h2 className="nhsuk-error-summary__title" id="error-summary-title">
                                There is a problem
                            </h2>
                            <div className="nhsuk-error-summary__body">
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    <Card>
                        <Card.Content>
                            <Card.Heading>Request Access</Card.Heading>
                            
                            <div style={{ marginBottom: '24px', padding: '16px', background: '#f0f4f5', borderRadius: '4px' }}>
                                <p style={{ marginBottom: '8px' }}>
                                    <strong>Requesting as:</strong> {userInfo.email}
                                </p>
                                {userInfo.username && (
                                    <p style={{ marginBottom: '0' }}>
                                        <strong>Name:</strong> {userInfo.username}
                                    </p>
                                )}
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="nhsuk-form-group">
                                    <label className="nhsuk-label" htmlFor="nhsNumber">
                                        <strong>Patient NHS Number</strong>
                                    </label>
                                    <span className="nhsuk-hint">
                                        Enter the 10-digit NHS number (e.g., 123 456 7890)
                                    </span>
                                    <input
                                        className="nhsuk-input nhsuk-input--width-20"
                                        id="nhsNumber"
                                        name="nhsNumber"
                                        type="text"
                                        value={nhsNumber}
                                        onChange={(e) => setNhsNumber(e.target.value)}
                                        placeholder="123 456 7890"
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div className="nhsuk-form-group">
                                    <label className="nhsuk-label" htmlFor="reason">
                                        <strong>Reason for Emergency Access</strong>
                                    </label>
                                    <span className="nhsuk-hint">
                                        Optional: Provide a brief reason (e.g., "Emergency cardiac assessment")
                                    </span>
                                    <textarea
                                        className="nhsuk-textarea"
                                        id="reason"
                                        name="reason"
                                        rows="3"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Brief description of emergency situation"
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? 'Requesting Access...' : 'Request Emergency Access'}
                                </Button>
                            </form>
                        </Card.Content>
                    </Card>
                </Container>
            </main>
        </div>
    );
}

export default EmergencyAccessRequestScreen;

